export const AFFILIATE_COMMISSION_PERCENT = 30;
export const AFFILIATE_COMMISSION_MONTHS = 12;
export const AFFILIATE_STORAGE_KEY = "agentreacher:affiliate";

export type AffiliateAttribution = {
	code: string;
	sourceParam: "ref" | "affiliate" | "via";
	capturedAt: string;
	landingPath: string;
};

const AFFILIATE_QUERY_PARAMS = ["ref", "affiliate", "via"] as const;
const AFFILIATE_CODE_MAX_LENGTH = 64;

export function normalizeAffiliateCode(value: unknown): string | null {
	if (typeof value !== "string") return null;

	const normalized = value.trim().toLowerCase();
	if (!normalized || normalized.length > AFFILIATE_CODE_MAX_LENGTH) {
		return null;
	}

	return /^[a-z0-9][a-z0-9_-]*$/.test(normalized) ? normalized : null;
}

export function readAffiliateAttributionFromUrl(
	url: URL,
	now = new Date(),
): AffiliateAttribution | null {
	for (const param of AFFILIATE_QUERY_PARAMS) {
		const code = normalizeAffiliateCode(url.searchParams.get(param));
		if (!code) continue;

		return {
			code,
			sourceParam: param,
			capturedAt: now.toISOString(),
			landingPath: `${url.pathname}${url.search}`,
		};
	}

	return null;
}

export function getStoredAffiliateAttribution(): AffiliateAttribution | null {
	if (typeof window === "undefined") return null;

	try {
		const rawValue = window.localStorage.getItem(AFFILIATE_STORAGE_KEY);
		if (!rawValue) return null;

		const parsed = JSON.parse(rawValue) as Partial<AffiliateAttribution>;
		const code = normalizeAffiliateCode(parsed.code);
		if (!code) return null;

		const sourceParam = AFFILIATE_QUERY_PARAMS.includes(
			parsed.sourceParam as AffiliateAttribution["sourceParam"],
		)
			? (parsed.sourceParam as AffiliateAttribution["sourceParam"])
			: "ref";

		return {
			code,
			sourceParam,
			capturedAt:
				typeof parsed.capturedAt === "string"
					? parsed.capturedAt
					: new Date().toISOString(),
			landingPath:
				typeof parsed.landingPath === "string" ? parsed.landingPath : "/",
		};
	} catch {
		return null;
	}
}

export function captureAffiliateAttributionFromLocation(): AffiliateAttribution | null {
	if (typeof window === "undefined") return null;

	const attribution = readAffiliateAttributionFromUrl(
		new URL(window.location.href),
	);
	if (!attribution) return getStoredAffiliateAttribution();

	try {
		window.localStorage.setItem(
			AFFILIATE_STORAGE_KEY,
			JSON.stringify(attribution),
		);
	} catch {
		// Affiliate attribution is best-effort and should not block the app.
	}

	return attribution;
}
